import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Card, Button, Input, Badge } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import DeleteConfirmModal from '../../components/ui/DeleteConfirmModal';
import SafeIcon from '../../common/SafeIcon';
import CopyButton from '../../components/ui/CopyButton';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import { api } from '../../lib/api';

const {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiZap,
  FiEye,
  FiEyeOff,
  FiClock,
  FiActivity,
  FiCopy,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
} = FiIcons;

const EMPTY_FORM = {
  name: '',
  project_ref: '',
  anon_key: '',
  ping_table: '_keepalive',
  notes: '',
  is_active: true,
};

const SETUP_INSTRUCTIONS_SQL = `create table if not exists public._keepalive (
  id int primary key default 1,
  pinged_note text default 'keepalive ping target - safe to ignore'
);

insert into public._keepalive (id) values (1) on conflict (id) do nothing;

alter table public._keepalive enable row level security;

create policy "anon_can_read_keepalive" on public._keepalive 
  for select using (true);`;

const KEEPALIVE_TABLE_SQL = `create table if not exists public._keepalive (
  id int primary key default 1,
  pinged_note text default 'keepalive ping target - safe to ignore'
);
insert into public._keepalive (id)
values (1)
on conflict (id) do nothing;
alter table public._keepalive enable row level security;
drop policy if exists "anon_can_read_keepalive" on public._keepalive;
create policy "anon_can_read_keepalive"
  on public._keepalive
  for select
  using (true);`;

function formatDateTime(val) {
  if (!val) return '—';
  try {
    return format(new Date(val), 'MMM d, yyyy h:mm a');
  } catch {
    return '—';
  }
}

function SetupInstructionsPanel() {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden border border-primary/20 bg-primary/5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-primary/10 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-900">
            How to set up a new project for monitoring
          </span>
        </div>
        <SafeIcon
          icon={expanded ? FiChevronUp : FiChevronDown}
          className="w-4 h-4 text-gray-500 flex-shrink-0"
        />
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-primary/10">
          <p className="text-sm text-gray-600 mt-3">
            Run this SQL once in the Supabase SQL Editor of each project you want to monitor.
            This creates a lightweight ping target table that the Keep-Alive Monitor will hit every 3 days.
          </p>
          <div className="relative mt-3">
            <div className="absolute top-2 right-2 z-10">
              <CopyButton
                text={SETUP_INSTRUCTIONS_SQL}
                title="Copy SQL"
                className="w-7 h-7 bg-white border border-gray-200 rounded shadow-sm text-gray-500 hover:text-primary"
              />
            </div>
            <pre className="bg-slate-900 text-slate-100 border border-slate-700 rounded-md p-4 pr-12 text-xs font-mono whitespace-pre overflow-x-auto">
              {SETUP_INSTRUCTIONS_SQL}
            </pre>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            After running the SQL, come back here and add the project using the Add Project button.
            Set the Ping Table field to{' '}
            <code className="font-mono text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded text-primary">
              _keepalive
            </code>
            .
          </p>
        </div>
      )}
    </Card>
  );
}

function PingStatusBadge({ lastPing }) {
  if (!lastPing) {
    return <Badge variant="default">Never pinged</Badge>;
  }
  if (lastPing.status === 'success') {
    return <Badge variant="success">Success</Badge>;
  }
  return <Badge variant="danger">Failed</Badge>;
}

function ProjectFormModal({ isOpen, onClose, project, onSaved }) {
  const { addToast } = useToast();
  const isEdit = !!project?.id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAnonKey, setShowAnonKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setShowAnonKey(false);
    if (project) {
      setForm({
        name: project.name || '',
        project_ref: project.project_ref || '',
        anon_key: project.anon_key || '',
        ping_table: project.ping_table || '',
        notes: project.notes || '',
        is_active: project.is_active !== false,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen, project]);

  const handleCopySql = async () => {
    try {
      await navigator.clipboard.writeText(KEEPALIVE_TABLE_SQL);
      addToast({ type: 'success', title: 'Copied!' });
    } catch {
      addToast({ type: 'error', title: 'Copy failed', message: 'Could not copy to clipboard.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.project_ref.trim() || !form.anon_key.trim() || !form.ping_table.trim()) {
      addToast({ type: 'warning', title: 'Please fill in all required fields' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        project_ref: form.project_ref.trim(),
        anon_key: form.anon_key.trim(),
        ping_table: form.ping_table.trim(),
        notes: form.notes.trim() || null,
        is_active: !!form.is_active,
      };

      if (isEdit) {
        await api.monitoredProjects.update(project.id, payload);
        addToast({ type: 'success', title: 'Project updated' });
      } else {
        await api.monitoredProjects.create(payload);
        addToast({ type: 'success', title: 'Project added' });
      }
      onSaved();
      onClose();
    } catch (err) {
      addToast({
        type: 'error',
        title: isEdit ? 'Failed to update project' : 'Failed to add project',
        message: err?.message || 'Try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Edit — ${project?.name}` : 'Add Monitored Project'}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-3 mt-3">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="My Supabase Project"
        />
        <Input
          label="Project Ref"
          required
          value={form.project_ref}
          onChange={(e) => setForm((f) => ({ ...f, project_ref: e.target.value }))}
          placeholder="abcdefghijklmnop"
        />
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Anon Key</label>
          <div className="relative">
            <input
              type={showAnonKey ? 'text' : 'password'}
              required
              value={form.anon_key}
              onChange={(e) => setForm((f) => ({ ...f, anon_key: e.target.value }))}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowAnonKey((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              title={showAnonKey ? 'Hide key' : 'Show key'}
            >
              <SafeIcon icon={showAnonKey ? FiEyeOff : FiEye} className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="w-full">
          <Input
            label="Ping Table"
            required
            value={form.ping_table}
            onChange={(e) => setForm((f) => ({ ...f, ping_table: e.target.value }))}
            placeholder="_keepalive"
          />
          <p className="mt-2 text-xs text-gray-500">
            Run this once in the target project&apos;s SQL Editor:
          </p>
          <div className="relative mt-1">
            <button
              type="button"
              onClick={() => void handleCopySql()}
              className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-800"
            >
              <SafeIcon icon={FiCopy} className="w-3 h-3" />
              Copy
            </button>
            <pre className="bg-gray-50 border border-gray-200 rounded p-3 pr-16 text-xs font-mono text-gray-700 whitespace-pre overflow-x-auto">
              {KEEPALIVE_TABLE_SQL}
            </pre>
          </div>
        </div>
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2}
            placeholder="Optional notes…"
            className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700">Active</span>
        </label>
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? <><Spinner className="mr-2" />Saving…</> : isEdit ? 'Save changes' : 'Add project'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PingHistoryModal({ project, isOpen, onClose }) {
  const { addToast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !project?.id) return;
    setLoading(true);
    api.monitoredProjects.getPingHistory(project.id, 20)
      .then(setLogs)
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Failed to load ping history',
          message: err?.message || 'Try again.',
        });
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, project?.id, addToast]);

  if (!project) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Ping History — ${project.name}`} className="max-w-lg">
      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2">
          <Spinner className="text-primary" />
          <span className="text-sm text-gray-500">Loading…</span>
        </div>
      ) : logs.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No ping logs yet.</p>
      ) : (
        <div className="mt-3 max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">HTTP</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="bg-white">
                  <td className="px-3 py-2">
                    {log.status === 'success' ? (
                      <Badge variant="success">Success</Badge>
                    ) : (
                      <Badge variant="danger">Failed</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{log.http_status ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateTime(log.pinged_at)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[140px]" title={log.error_message || ''}>
                    {log.error_message || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function KeepAliveTab() {
  const { addToast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [historyProject, setHistoryProject] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [pingingId, setPingingId] = useState(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.monitoredProjects.getAll();
      setProjects(data ?? []);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to load projects',
        message: err?.message || 'Try again.',
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleToggleActive = async (project) => {
    setTogglingId(project.id);
    try {
      const updated = await api.monitoredProjects.update(project.id, {
        is_active: !project.is_active,
      });
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, ...updated, last_ping: p.last_ping } : p)),
      );
      addToast({
        type: 'success',
        title: updated.is_active ? 'Project activated' : 'Project deactivated',
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to update project',
        message: err?.message || 'Try again.',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleTestPing = async (project) => {
    setPingingId(project.id);
    try {
      const result = await api.monitoredProjects.testPing(project.id);
      if (result.success) {
        addToast({ type: 'success', title: 'Ping successful', message: result.message });
        loadProjects();
      } else {
        addToast({
          type: 'error',
          title: 'Ping failed',
          message: result.message || 'Edge Function not yet connected',
        });
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Ping failed',
        message: err?.message || 'Edge Function not yet connected',
      });
    } finally {
      setPingingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await api.monitoredProjects.delete(deleteTarget.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      addToast({ type: 'success', title: 'Project deleted' });
      setDeleteTarget(null);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to delete project',
        message: err?.message || 'Try again.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (project) => {
    setEditProject(project);
    setFormOpen(true);
  };

  const openAdd = () => {
    setEditProject(null);
    setFormOpen(true);
  };

  return (
    <div className="space-y-4">
      <SetupInstructionsPanel />

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {projects.length} external project{projects.length !== 1 ? 's' : ''} monitored
        </p>
        <Button size="sm" onClick={openAdd}>
          <SafeIcon icon={FiPlus} className="mr-1.5 w-4 h-4" />
          Add Project
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2">
            <Spinner size="lg" className="text-primary" />
            <p className="text-sm text-gray-500">Loading projects…</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
              <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No monitored projects</p>
            <p className="text-xs text-gray-400 mt-1">Add an external Supabase project to keep it alive.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Project Ref</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Ping Table</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Active</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Last Ping</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Last Ping Time</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projects.map((project) => (
                  <tr key={project.id} className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{project.name}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{project.project_ref}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{project.ping_table}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        disabled={togglingId === project.id}
                        onClick={() => handleToggleActive(project)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 ${
                          project.is_active ? 'bg-primary' : 'bg-gray-200'
                        }`}
                        title={project.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${
                            project.is_active ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <PingStatusBadge lastPing={project.last_ping} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDateTime(project.last_ping?.pinged_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(project)}
                          title="Edit"
                        >
                          <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestPing(project)}
                          disabled={pingingId === project.id}
                          title="Test Ping"
                        >
                          {pingingId === project.id ? (
                            <Spinner className="w-3.5 h-3.5" />
                          ) : (
                            <SafeIcon icon={FiZap} className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryProject(project)}
                          title="Ping History"
                        >
                          <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteTarget(project)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ProjectFormModal
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditProject(null); }}
        project={editProject}
        onSaved={loadProjects}
      />

      <PingHistoryModal
        project={historyProject}
        isOpen={!!historyProject}
        onClose={() => setHistoryProject(null)}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        leadName={deleteTarget?.name || 'this project'}
        isDeleting={deleting}
      />
    </div>
  );
}
